import type { processedAddressObj } from "@/domains/addressLookups/types";
import Coordinate from "@/domains/coordinates/coordinate";
import generateUniqueLocalID from "@/utils/generateUniqueLocalID";

/** Persisted shape of a `TripLocationCore`, stored inside a route plan's `planning_parameters`. */
export type TripLocationCoreDTO = {
  address: string;
  latitude: number;
  longitude: number;
  name?: string;
  stay?: number;
  time?: string;
  date?: string;
  weightChange?: number;
  savedLocationExternalId?: string;
};

export type TripLocationCoreOptions = {
  /**
   * coordinate object for this location.
   */
  coordinates: Coordinate;

  /**
   * Display String for location.
   */
  address: string;

  /**
   * Name of this location.
   */
  name?: string;

  /**
   * The external ID of the saved location this location originated from, if any.
   *
   * Used to preserve the relationship to a persisted SavedLocation without
   * embedding the SavedLocation object itself.
   */
  savedLocationExternalId?: string;
};

export class TripLocationCore {
  // ----------------------------------------------------------------------- //
  // -------------------------- Global class state ------------------------- //
  // ----------------------------------------------------------------------- //

  /**
   * global record of class instance ids this session.
   */
  static usedIds: string[] = [];

  // ----------------------------------------------------------------------- //
  // ------------------------------- State --------------------------------- //
  // ----------------------------------------------------------------------- //

  /**
   * local session scope unique id for this trip.
   */
  id: string;

  /**
   * Display String for location.
   */
  address: string;

  /**
   * Lat/Lon coordinate object for this location.
   */
  coordinates: Coordinate;

  /**
   * Name of this location.
   */
  name?: string;

  /**
   * Number of seconds the user is intending to stay at this location. This is
   * an optional property only used for additional stops calculating times
   * taking into account this extra duration of the stay at this location.
   */
  stay?: number;

  /**
   * The arrival time for this location. If no time is set it will appear as an
   * empty string
   */
  time?: string;

  /**
   * Optional property used for calculations of overnight trips.
   */
  date?: string;

  /**
   * Weight change for the load carried at this scheduled stop in kgs.
   *
   * NOTE: this could be either a positive or negative depending on if it is an
   * expected pick up or drop off.
   */
  weightChange?: number;

  /**
   * The external ID of the saved location this location originated from, if any.
   *
   * Used to preserve the relationship to a persisted SavedLocation without
   * embedding the SavedLocation object itself.
   */
  savedLocationExternalId?: string;

  // ----------------------------------------------------------------------- //
  // ---------------------------- Constructor ------------------------------ //
  // ----------------------------------------------------------------------- //

  constructor(options: TripLocationCoreOptions) {
    this.id = generateUniqueLocalID(TripLocationCore.usedIds, "trip-location");
    this.coordinates = options.coordinates;
    this.address = options.address;
    this.name = options.name;
    this.savedLocationExternalId = options.savedLocationExternalId;
    TripLocationCore.usedIds.push(this.id);
  }

  /**
   * Reconstructs a `TripLocationCore` from its persisted DTO shape (see
   * `toDTO`). `stay`/`time`/`date`/`weightChange` aren't constructor options
   * (see `clone()` above), so they're assigned post-construction here too.
   */
  public static fromDTO(dto: TripLocationCoreDTO): TripLocationCore {
    const location = new TripLocationCore({
      address: dto.address,
      coordinates: new Coordinate({
        latitude: dto.latitude,
        longitude: dto.longitude,
      }),
      name: dto.name,
      savedLocationExternalId: dto.savedLocationExternalId,
    });
    location.stay = dto.stay;
    location.time = dto.time;
    location.date = dto.date;
    location.weightChange = dto.weightChange;
    return location;
  }

  // ----------------------------------------------------------------------- //
  // ------------------------------- Methods -------------------------------- //
  // ----------------------------------------------------------------------- //

  /**
   * Projects this location into the shape the address-autocomplete widgets
   * display/consume. Display-only: stay/weightChange/savedLocationExternalId
   * etc. are not part of that shape, so this is a one-way projection, not
   * where the data is stored.
   */
  public get toProcessedAddressObj(): processedAddressObj {
    return {
      address: this.address,
      coordinates: this.coordinates,
      name: this.name,
    };
  }

  /**
   * Returns a copy of this location as a new, independent instance (a fresh
   * `id`, since a clone is a new draft until it's committed to a trip).
   *
   * `stay`/`time`/`date`/`weightChange` aren't `TripLocationCoreOptions` (they
   * can only be set after construction - see WaypointPlanningCard.vue/
   * WaypointEditForm.vue), so they have to be copied over explicitly here
   * too, or a clone silently loses them. This matters in practice:
   * tripStore.seedPlanningFromSelectedTrip() clones every location when
   * re-entering the planning flow for an existing trip, and
   * PlanningAddDetailsView.vue's arrival/departure date+time fields mutate
   * `date`/`time` directly on those clones.
   */
  public clone(): TripLocationCore {
    const cloned = new TripLocationCore({
      address: this.address,
      coordinates: this.coordinates,
      name: this.name,
      savedLocationExternalId: this.savedLocationExternalId,
    });
    cloned.stay = this.stay;
    cloned.time = this.time;
    cloned.date = this.date;
    cloned.weightChange = this.weightChange;
    return cloned;
  }

  /** Persisted shape of this location, for storing inside a route plan's `planning_parameters`. */
  public compileDTO(): TripLocationCoreDTO {
    return {
      address: this.address,
      latitude: this.coordinates.latitude,
      longitude: this.coordinates.longitude,
      name: this.name,
      stay: this.stay,
      time: this.time,
      date: this.date,
      weightChange: this.weightChange,
      savedLocationExternalId: this.savedLocationExternalId,
    };
  }
}
